// @ts-nocheck
import { useForm, useStore, revalidateLogic } from "@tanstack/react-form";
import { useState, useRef, useCallback, useEffect } from "react";
import {
  orderFormSchema,
  type OrderFormData,
  type OrderDetailFormData,
} from "../../../shared/schemas/order.schema";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/shared/FormattedNumberInput";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, safeFormatDateInput, validateUploadFile, validateExcelFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from "@/lib/utils";
import { focusFirstError, hasFieldError, collectFormErrorMessages } from "../../../lib/form/formUtils";
import {
  Save,
  Loader2,
  FileText,
  Truck,
  Package,
  Upload,
  FileDown,
  Info,
  AlertTriangle,
  Flame,
  Zap,
  UserCog,
} from "lucide-react";
import CustomerFormModal from "../../master/customers/CustomerFormModal";
import ProductFormModal from "../../master/products/ProductFormModal";
import ProductEditModal from "../../master/products/ProductEditModal";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import CurrencyExchangeField from "@/components/shared/CurrencyExchangeField";
import { formatMoney } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

import { ExternalLink, Edit, Copy } from "lucide-react";
import { toast } from "sonner";
import InlineDetailTable, {
  type DetailColumn,
} from "@/components/shared/InlineDetailTable";
import { SmartImportStagingModal } from "@/components/shared/SmartImportStagingModal";
import {
  downloadExcelTemplate,
  parseExcelFile,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { EditableCombobox } from "@/components/shared/EditableCombobox";
import { productsApi } from "@/lib/api-client";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { OrderPriority, SampleType } from "@/config/status-registry";
import { uploadFile, getFullUrl } from "@/lib/uploadFile";
import { NumericInput } from "@/components/shared/NumericInput";
import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import { useAuthStore } from "@/stores/auth.store";

import { trpcClient } from "@/lib/trpc";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { createDebouncedRowCostEstimator } from "./order-cost-estimator.helper";
import { formatProductSpecs } from "@/lib/format-specs";
import { useFormBlocker } from "@/hooks/useFormBlocker";

interface OrderFormProps {
  defaultValues?: Partial<OrderFormData>;
  originalOrderCreatedAt?: string;
  linkedQuotation?: { version: number; updatedAt: string };
  onSubmit: (data: OrderFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

/* ── Helpers ── */

const formatSpecs = (specsRaw: any, product?: any): string => formatProductSpecs(specsRaw, product);

const EMPTY_DETAIL: OrderDetailFormData = {
  productId: "",
  productCode: "",
  productName: "",
  specs: "",
  process: "",
  unit: "tờ",
  quantity: 0,
  unitPrice: 0,
  amount: 0,
  costPrice: 0,
  profitMargin: 0,
  allowancePercent: 0,
  stockQty: 0,
  lsxQty: 0,
  notes: "",
  poReference: "",
  soReference: "",
};

// Columns definition moved inside component to access product lookup

const DETAIL_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "productCode", label: "Mã SP" },
  { key: "productName", label: "Tên SP", required: true },
  { key: "poReference", label: "Số PO" },
  { key: "soReference", label: "Số SO" },
  { key: "specs", label: "Quy cách" },
  { key: "unit", label: "Đơn vị" },
  { key: "quantity", label: "Số lượng", type: "int", required: true },
  { key: "unitPrice", label: "Đơn giá", type: "decimal", required: true },
  { key: "costPrice", label: "Giá gốc", type: "decimal" },
  { key: "notes", label: "Ghi chú" },
];

const ORDER_SAMPLE_DATA = [
  {
    productCode: "VNF-091644",
    productName: "Hộp COLOR BOX",
    poReference: "PO-001",
    soReference: "SO-001",
    specs: "Duplex450gsm W175*D175*H160mm",
    unit: "PCS",
    quantity: 20000,
    unitPrice: 10700,
    costPrice: 8500,
    notes: "Dữ liệu mẫu",
  }
];

export default function OrderForm({
  defaultValues,
  originalOrderCreatedAt,
  linkedQuotation,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
}: OrderFormProps) {
  const currentUser = useAuthStore((s) => s.user);
  const { hasPermission } = useRoleAccess();

  // Auto-calculate delivery deadline = orderDate + 7 days (standard lead time)
  const calcDeadline = (orderDateStr: unknown, daysToAdd = 7) => {
    const safeDate = safeFormatDateInput(orderDateStr) || safeFormatDateInput(new Date());
    const d = new Date(safeDate);
    d.setDate(d.getDate() + daysToAdd);
    return safeFormatDateInput(d);
  };

  const initialOrderDate =
    safeFormatDateInput(defaultValues?.orderDate) || safeFormatDateInput(new Date());
  const initialDeadline =
    safeFormatDateInput(defaultValues?.deliveryDeadline) || calcDeadline(initialOrderDate, 7);
  const initialOrderDetails = defaultValues?.orderDetails?.map(d => ({
    ...d,
    deliveryDeadline: safeFormatDateInput(d.deliveryDeadline) || initialDeadline
  })) ?? [{ ...EMPTY_DETAIL, deliveryDeadline: initialDeadline }];

  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1)
  const currentStatus = (defaultValues as any)?.status as string | undefined;
  const saveModeRef = useRef<"DRAFT" | "SUBMIT">("SUBMIT");
  const [isDraftSaving, setIsDraftSaving] = useState(false);

  const form = useForm({
    defaultValues: {
      orderId: defaultValues?.orderId ?? "",
      customerId: defaultValues?.customerId ?? "",
      customerName: defaultValues?.customerName ?? "",
      contactPerson: defaultValues?.contactPerson ?? "",
      phone: defaultValues?.phone ?? "",
      email: defaultValues?.email ?? "",
      updateCustomerInfo: defaultValues?.updateCustomerInfo ?? false,
      orderDate: initialOrderDate,
      deliveryDeadline: initialDeadline,
      approvedDate: (defaultValues as any)?.approvedDate ?? "",
      orderDetails: initialOrderDetails,
      vatRate: defaultValues?.vatRate ?? 8,
      shippingFee: defaultValues?.shippingFee ?? 0,
      currency: defaultValues?.currency ?? ("VND" as const),
      exchangeRate: defaultValues?.exchangeRate ?? 1,
      deposit: defaultValues?.deposit ?? "",
      paymentTerm: defaultValues?.paymentTerm ?? "",
      deliveryAddress: defaultValues?.deliveryAddress ?? "",
      deliveryMethod: (defaultValues as any)?.deliveryMethod ?? "",
      deliveryNotes: (defaultValues as any)?.deliveryNotes ?? "",
      status: defaultValues?.status ?? ("DRAFT" as const),
      category: (defaultValues as any)?.category ?? "MANUFACTURING",
      priority: (defaultValues as any)?.priority ?? "NORMAL",
      orderType: (defaultValues as any)?.orderType ?? "PRODUCTION",
      sampleType: (defaultValues as any)?.sampleType ?? "",
      notes: defaultValues?.notes ?? "",
      salesUserId: defaultValues?.salesUserId ?? currentUser?.id ?? "",
    },
    // @ts-ignore
    // Validate khi bấm lưu, sau lần bấm đầu tiên tự kiểm tra lại mỗi khi nhập để lỗi biến mất ngay khi đã sửa
    validationLogic: revalidateLogic({ mode: "submit", modeAfterSubmission: "change" }),
    validators: { onDynamic: orderFormSchema as any },
    onSubmit: async ({ value }) => onSubmit({ ...(value as OrderFormData), saveMode: saveModeRef.current } as any),
  });

  useEffect(() => {
    if (currentUser?.id) {
      const currentSalesUserId = form.getFieldValue("salesUserId");
      if (!currentSalesUserId) {
        form.setFieldValue("salesUserId", currentUser.id);
      }
    }
  }, [currentUser?.id, form]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    saveModeRef.current = "SUBMIT";
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  const isDirty = useStore(form.store, (state: any) => state.isDirty);
  const isSubmittingForm = useStore(
    form.store,
    (state: any) => state.isSubmitting,
  );
  useFormBlocker(isDirty && !isSubmittingForm && !isSubmitting && !isDraftSaving);

  // Lưu nháp: ghi tạm thông tin đang có (chỉ cần khách hàng), không kiểm tra đủ thông tin
  const handleSaveDraft = async () => {
    const values = form.state.values as any;
    if (!values.customerId) {
      toast.error("Chọn khách hàng trước khi lưu nháp");
      return;
    }
    saveModeRef.current = "DRAFT";
    setIsDraftSaving(true);
    try {
      await onSubmit({ ...values, saveMode: "DRAFT" } as any);
    } finally {
      setIsDraftSaving(false);
      saveModeRef.current = "SUBMIT";
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const costEstimatorRef = useRef<ReturnType<
    typeof createDebouncedRowCostEstimator<OrderDetailFormData>
  > | null>(null);
  if (!costEstimatorRef.current) {
    costEstimatorRef.current = createDebouncedRowCostEstimator<OrderDetailFormData>({
      debounceMs: 500,
      estimateCost: (productId, quantity) =>
        productsApi.estimateCost(productId, quantity),
      getRows: () =>
        (form.getFieldValue("orderDetails" as any) as OrderDetailFormData[]) || [],
      applyRowCost: (rowIndex, nextUnitCost) => {
        const currentRows =
          (form.getFieldValue("orderDetails" as any) as OrderDetailFormData[]) || [];
        if (currentRows[rowIndex]) {
          const updatedRows = [...currentRows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            costPrice: nextUnitCost,
          };
          form.setFieldValue("orderDetails" as any, updatedRows as any);
        }
      },
    });
  }

  useEffect(() => {
    return () => {
      costEstimatorRef.current?.dispose();
      costEstimatorRef.current = null;
    };
  }, []);
  const [importing, setImporting] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [stagingModalOpen, setStagingModalOpen] = useState(false);
  const [stagingRawRows, setStagingRawRows] = useState<any[]>([]);
  const [importFileName, setImportFileName] = useState("");

  const handleImportDetails = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateExcelFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setImporting(true);
    try {
      const result = await parseExcelFile(file);
      if (result.rows.length === 0) {
        toast.error("File không có dữ liệu");
        return;
      }
      
      const payloadItems: any[] = [];
      result.rows.forEach((row) => {
        const rawPo = String(
          row["Số PO"] || row["PO"] || row["PO Number"] || row["poReference"] || ""
        ).trim();
        const rawSo = String(
          row["Số SO"] || row["SO"] || row["SO Number"] || row["soReference"] || ""
        ).trim();

        // Split by comma or semicolon
        const poList = rawPo ? rawPo.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [""];
        const soList = rawSo ? rawSo.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [""];

        poList.forEach((po) => {
          soList.forEach((so) => {
            const quantity = parseInt(row["Số lượng"] || row["quantity"] || "0", 10);
            const unitPrice = parseFloat(
              String(row["Đơn giá"] || row["unitPrice"] || "0").replace(/,/g, "")
            );
            payloadItems.push({
              productCode: String(row["Mã SP"] || row["productCode"] || "").trim(),
              productName: String(row["Tên SP"] || row["productName"] || "").trim(),
              customerId: selectedCustomerId,
              poReference: po.substring(0, 50),
              soReference: so.substring(0, 50),
              customerPo: String(row["Customer PO"] || row["customerPo"] || row["PO Khách"] || "").trim(),
              specs: String(row["Quy cách"] || row["specs"] || "").trim(),
              unit: String(row["Đơn vị"] || row["unit"] || "hộp"),
              quantity,
              unitPrice,
              notes: String(row["Ghi chú"] || row["notes"] || ""),
              costPrice: parseFloat(String(row["Giá gốc"] || row["costPrice"] || "0").replace(/,/g, "")) || undefined,
            });
          });
        });
      });

      setImportFileName(file.name);
      setStagingRawRows(payloadItems);
      setStagingModalOpen(true);
    } catch (err: any) {
      toast.error(`Lỗi đọc file: ${extractErrorMessage(err)}`);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleStagingSuccess = ({ orderDetails }: { orderDetails: any[] }) => {
    const currentDetails =
      (form.getFieldValue("orderDetails") as OrderDetailFormData[]) || [];
    const nonEmpty = currentDetails.filter(
      (d) => d.productName || d.productCode,
    );
    
    const newRows = [...nonEmpty, ...orderDetails];
    form.setFieldValue("orderDetails", newRows as any);
    
    // Auto-recalculate amounts and costs if necessary
    const currentCategory = form.getFieldValue("category");
    if (currentCategory === "COMMERCIAL") {
      const totalCost = newRows.reduce((sum, row: any) => sum + ((row.quantity || 0) * (row.costPrice || 0)), 0);
      form.setFieldValue("totalCostPrice" as any, totalCost);
    }

    toast.success(`Đã đưa ${orderDetails.length} dòng sản phẩm vào đơn hàng`);
  };

  const queryClient = useQueryClient();
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingProductRowIndex, setEditingProductRowIndex] = useState<number | null>(null);
  const [cloningProduct, setCloningProduct] = useState<{
    id: string;
    productCode: string;
    productName: string;
    customerId?: string | null;
    customer?: { name?: string; shortName?: string } | null;
  } | null>(null);
  const [cloningRowIndex, setCloningRowIndex] = useState<number | null>(null);

  const handleCloneProductSuccess = (newProduct: any) => {
    const currentRows = (form.getFieldValue("orderDetails") as OrderDetailFormData[]) || [];
    const sourceRow = cloningRowIndex !== null ? currentRows[cloningRowIndex] : null;
    const orderCurrency = (form.getFieldValue("currency") as string) || "VND";

    const specsStr = formatSpecs(newProduct.specs, newProduct) || newProduct.packingSpec || sourceRow?.specs || "";
    const processStr = newProduct.process || sourceRow?.process || "";
    const catalogPrice = Number(newProduct.price || newProduct.costPrice || 0);

    const newRow: OrderDetailFormData = {
      productId: newProduct.id,
      productCode: newProduct.productCode || "",
      productName: newProduct.productName || newProduct.name || "",
      unit: newProduct.unit || sourceRow?.unit || "tờ",
      specs: specsStr,
      process: processStr,
      materialId: newProduct.materialId || sourceRow?.materialId || "",
      poReference: sourceRow?.poReference || "",
      soReference: sourceRow?.soReference || "",
      quantity: 0, // Mặc định bằng 0 theo thiết kế thống nhất
      unitPrice: sourceRow?.unitPrice || catalogPrice,
      costPrice: Number(newProduct.costPrice || sourceRow?.costPrice || 0),
      amount: 0,
      notes: sourceRow?.notes || "",
      allowancePercent: sourceRow?.allowancePercent ?? currentTolerance,
      deliveryDeadline: sourceRow?.deliveryDeadline || form.getFieldValue("deliveryDeadline"),
    };

    if (orderCurrency !== "VND") {
      newRow.priceUSD = sourceRow?.priceUSD || catalogPrice;
      newRow.amount = 0;
    }

    const updatedRows = [...currentRows];
    // Chèn ngay bên dưới dòng sản phẩm vừa copy (INV-ORDER-03)
    const insertIndex = cloningRowIndex !== null ? cloningRowIndex + 1 : updatedRows.length;
    updatedRows.splice(insertIndex, 0, newRow);
    form.setFieldValue("orderDetails" as any, updatedRows as any);

    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["entity-select", "products"] });

    toast.success(`Đã nhân bản và thêm sản phẩm "${newProduct.productName || newProduct.name}" vào đơn hàng!`);
    setCloningProduct(null);
    setCloningRowIndex(null);
  };

  const [currentTolerance, setCurrentTolerance] = useState(1.0);
  const paymentTermSuggestions = useConfigSuggestions("PAYMENT_TERMS", [
    "Net 30",
    "Net 60",
    "Net 90",
    "COD",
    "TT trước 50%",
    "TT trước 100%",
    "LC",
    "DA",
    "Theo hợp đồng",
  ]);
  const deliveryMethodSuggestions = useConfigSuggestions("DELIVERY_METHODS", [
    "Giao tận nơi",
    "Khách tự đến lấy",
    "Chuyển phát nhanh",
    "Vận tải đường bộ",
    "Vận tải đường biển",
  ]);

  // Track selected customerId for product filtering
  const [selectedCustomerId, setSelectedCustomerId] = useState(
    defaultValues?.customerId || "",
  );

  const canAddMasterData = !!currentUser;

  const getDetailColumns = useCallback(
    (currency: string): DetailColumn<OrderDetailFormData>[] => {
      const cols: DetailColumn<OrderDetailFormData>[] = [
        {
          key: "productId",
          label: "Sản phẩm / Quy cách",
          width: "min-w-[148px]",
          required: true,
          render: (_v, row, i, updateRow) => {
            const displayLabel = row.productName
              ? `${row.productCode ? `[${row.productCode}] ` : ""}${row.productName}`
              : "";

            return (
              // Responsive bounded width: long product names truncate cleanly without forcing horizontal scroll (FB-LWU401 & FB-90M4OH);
              // màn rộng (≥1536px) dư chỗ thì ô rộng hơn để đọc đủ mã / tên (FB-741TR7)
              <div className="flex flex-col gap-1 py-0.5 w-full min-w-[144px] max-w-[195px] 2xl:min-w-[240px] 2xl:max-w-[300px]">
                <SharedCombobox
                  type="product"
                  params={{ customerId: selectedCustomerId }}
                  value={row.productId || ""}
                  valueLabel={displayLabel}
                  placeholder="Chọn sản phẩm từ danh mục..."
                  breadcrumb={["Đơn hàng", "Chi tiết", "Sản phẩm"]}
                  onChange={(id, selectedObj) => {
                    if (id) {
                      const orig = (selectedObj as any)?.original || selectedObj || {};
                      const formattedSpecs = formatSpecs(orig.specs, orig);
                      const specsStr =
                        formattedSpecs ||
                        [
                          orig.length && orig.width
                            ? `${orig.length}x${orig.width}${orig.height ? `x${orig.height}` : ""}mm`
                            : "",
                          orig.material?.name || orig.material?.materialId || orig.paperType || "",
                          orig.printColors || "",
                        ]
                          .filter(Boolean)
                          .join(" • ") ||
                        (typeof orig.packingSpec === "string" ? orig.packingSpec : "") ||
                        "";

                      const catalogPrice = Number(
                        orig.sellingPrice || orig.price || orig.priceVND || orig.unitPrice || 0
                      );
                      const catalogCost = Number(orig.costPrice || 0);
                      const p =
                        currency !== "VND"
                          ? Number(orig.priceUSD || row.priceUSD || 0)
                          : catalogPrice || row.unitPrice || 0;

                      updateRow({
                        productId: id,
                        productCode: orig.productCode || orig.code || "",
                        productName: orig.productName || (selectedObj as any)?.label || orig.name || "",
                        unit: orig.unit || row.unit || "Cái",
                        specs: specsStr,
                        process: orig.routingSummary || orig.process || row.process || "",
                        materialId: orig.materialId || row.materialId || "",
                        unitPrice: p,
                        priceVND: catalogPrice || row.priceVND || 0,
                        priceUSD: Number(orig.priceUSD || row.priceUSD || 0),
                        costPrice: catalogCost,
                        amount: (row.quantity || 0) * p,
                      });
                    } else {
                      updateRow({
                        productId: "",
                        productCode: "",
                        productName: "",
                        specs: "",
                        process: "",
                        materialId: "",
                        unitPrice: 0,
                        amount: 0,
                      });
                    }
                  }}
                  error={!row.productId && !row.productName}
                />
                {(row.specs || row.process || row.productId) && (
                  <div className="flex items-center justify-between gap-1.5 text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                    <span className="truncate flex-1 font-mono text-[10.5px]">
                      {typeof row.specs === "string" ? row.specs : formatSpecs(row.specs, row)}
                    </span>
                    {row.productId && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProductId(row.productId || null);
                            setEditingProductRowIndex(i);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-[10px] font-medium"
                          title="Chỉnh sửa sản phẩm gốc"
                        >
                          [Sửa]
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCloningProduct({
                              id: row.productId!,
                              productCode: row.productCode || "",
                              productName: row.productName || "",
                              customerId: selectedCustomerId,
                            });
                            setCloningRowIndex(i);
                          }}
                          className="text-emerald-600 hover:text-emerald-800 hover:underline text-[10px] font-medium"
                          title="Nhân bản sản phẩm này thành mã mới"
                        >
                          [Nhân bản]
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          },
        },
        {
          key: "poReference",
          label: "Số PO",
          // Không bắt buộc: đơn lập từ báo giá thường chưa có PO khách (dấu * cũ gây hiểu nhầm, máy chủ không kiểm — 03/11/2026)
          width: "min-w-[60px]",
          render: (_v, row, i, updateRow) => (
            <Input
              size={1}
              placeholder="Nhập PO..."
              title={row.poReference || undefined}
              value={row.poReference || ""}
              maxLength={50}
              onChange={(e) => {
                const val = e.target.value;
                updateRow({ poReference: val });
              }}
              className={cn(
                // Số PO tới 16 ký tự (VD VNPO-2026-10151-): ô đủ rộng để đọc và sửa không bị cắt (FB-741TR7)
                "h-7 text-xs md:text-xs px-1 font-medium min-w-[128px]",
                !row.poReference
                  ? "border-red-300"
                  : "border-emerald-200 bg-emerald-50/30",
              )}
            />
          ),
        },
        {
          key: "soReference",
          label: "Số SO",
          width: "min-w-[54px]",
          render: (_v, row, _i, updateRow) => (
            <Input
              size={1}
              placeholder="SO"
              title={row.soReference || undefined}
              value={row.soReference || ""}
              maxLength={50}
              onChange={(e) => updateRow({ soReference: e.target.value })}
              className="h-7 text-xs md:text-xs px-1 2xl:min-w-[112px]"
            />
          ),
        },

        {
          key: "quantity",
          label: "SL đặt",
          width: "min-w-[58px]",
          align: "right",
          required: true,
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0"
              decimals={0}
              value={row.quantity || null}
              onChange={(val) => {
                const p =
                  currency !== "VND" ? row.priceUSD || 0 : row.unitPrice || 0;
                updateRow({ quantity: val || 0, amount: (val || 0) * p });
              }}
              className="h-7 text-xs md:text-xs px-1 text-right"
            />
          ),
        },
        {
          key: "stockQty",
          label: "SL Tồn",
          width: "min-w-[54px]",
          align: "right",
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0"
              decimals={0}
              value={row.stockQty || null}
              onChange={(val) => updateRow({ stockQty: val || 0 })}
              className="h-7 text-xs md:text-xs px-1 text-right"
            />
          ),
        },
        {
          key: "allowancePercent",
          label: "% Bù",
          width: "min-w-[46px]",
          align: "right",
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0"
              decimals={2}
              value={row.allowancePercent || null}
              onChange={(val) => updateRow({ allowancePercent: val || 0 })}
              className="h-7 text-xs md:text-xs px-1 text-right"
            />
          ),
        },
        {
          key: "lsxQty",
          label: "SL SX",
          type: "number",
          placeholder: "0",
          width: "min-w-[56px]",
          align: "right",
          readOnly: true,
          render: (_v, row) => {
            const qty = row.quantity || 0;
            const pct = row.allowancePercent || 0;
            const stock = row.stockQty || 0;
            const calc = Math.max(0, Math.ceil(qty * (1 + pct / 100)) - stock);
            row.lsxQty = calc; // Auto-update value for form submission
            return (
              <span className="px-1 py-1.5 block text-xs font-mono font-bold text-amber-600 truncate">
                {calc.toLocaleString("vi-VN")}
              </span>
            );
          },
        },
      ];

      if (currency !== "VND") {
        cols.push({
          key: "priceUSD",
          label: `Giá ${currency}`,
          width: "min-w-[90px]",
          align: "right",
          required: true,
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0.00"
              decimals={2}
              value={row.priceUSD || null}
              onChange={(val) => {
                updateRow({
                  priceUSD: val || 0,
                  amount: (row.quantity || 0) * (val || 0),
                });
              }}
              className="h-7 text-xs md:text-xs px-2 text-right"
            />
          ),
        });
        cols.push({
          key: "priceVND",
          label: "Quy đổi VND",
          width: "min-w-[100px]",
          align: "right",
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0"
              decimals={0}
              value={row.priceVND || null}
              onChange={(val) => updateRow({ priceVND: val || 0 })}
              className="h-7 text-xs md:text-xs px-2 text-right"
            />
          ),
        });
      } else {
        const currentCategory = form.getFieldValue("category" as any);
        if (currentCategory === "OUTSOURCE") {
          cols.push({
            key: "purchasePrice",
            label: "Giá mua",
            width: "min-w-[100px]",
            align: "right",
            render: (_v, row, _i, updateRow) => (
              <NumericInput
                size={1}
                placeholder="0"
                decimals={0}
                value={row.purchasePrice || null}
                onChange={(val) => {
                  updateRow({ purchasePrice: val || 0 });
                }}
                className="h-7 text-xs md:text-xs px-2 text-right border-amber-300 focus-visible:ring-amber-400"
              />
            ),
          });
        }
        if (currentCategory === "COMMERCIAL") {
          cols.push({
            key: "costPrice",
            label: "Giá gốc",
            width: "min-w-[100px]",
            align: "right",
            render: (_v, row, _i, updateRow) => (
              <NumericInput
                size={1}
                placeholder="0"
                decimals={0}
                value={row.costPrice || null}
                onChange={(val) => {
                  updateRow({ costPrice: val || 0 });
                }}
                className="h-7 text-xs md:text-xs px-2 text-right border-blue-300 focus-visible:ring-blue-400"
              />
            ),
          });
        }

        cols.push({
          key: "unitPrice",
          label: "Đơn giá",
          width: "min-w-[60px]",
          align: "right",
          required: true,
          render: (_v, row, _i, updateRow) => (
            <NumericInput
              size={1}
              placeholder="0"
              decimals={0}
              value={row.unitPrice || null}
              onChange={(val) => {
                updateRow({
                  unitPrice: val || 0,
                  amount: (row.quantity || 0) * (val || 0),
                });
              }}
              className="h-7 text-xs md:text-xs px-1 text-right"
            />
          ),
        });
      }

      cols.push({
        key: "amount",
        label: "Thành tiền",
        width: "min-w-[68px]",
        align: "right",
        readOnly: true,
        render: (_v, row) => {
          const p = currency !== "VND" ? row.priceUSD || 0 : row.unitPrice || 0;
          const amt = Number((row.quantity || 0) * p);
          row.amount = amt; // Auto-update for form submission
          return (
            <span className="px-1 py-1.5 block text-xs font-mono font-bold text-indigo-600 truncate">
              {amt.toLocaleString(currency !== "VND" ? "en-US" : "vi-VN", {
                minimumFractionDigits: currency !== "VND" ? 2 : 0,
                maximumFractionDigits: currency !== "VND" ? 2 : 0,
              })}
            </span>
          );
        },
      });

      cols.push({
        key: "deliveryDeadline",
        label: "Hạn giao",
        width: "min-w-[88px]",
        render: (_v, row, _i, updateRow) => (
          <Input
            size={1}
            type="date"
            value={safeFormatDateInput(row.deliveryDeadline || row.deliveryDate)}
            onChange={(e) =>
              updateRow({
                deliveryDeadline: e.target.value,
                deliveryDate: e.target.value,
                isCustomDeliveryDate: true,
              })
            }
            className="h-7 text-[11px] md:text-[11px] px-0.5 w-full min-w-[86px]"
            title="Hạn giao riêng cho từng dòng (nếu khác hạn giao chung)"
          />
        ),
      });

      cols.push({
        key: "notes",
        label: "Ghi chú",
        // Ghi chú dòng in lên Phiếu yêu cầu sản xuất (cột Ghi chú SX): ô nhiều dòng tự xuống dòng, kéo cao được
        width: "min-w-[54px]",
        render: (_v, row, _i, updateRow) => (
          <Textarea
            rows={1}
            placeholder="Ghi chú..."
            value={row.notes || ""}
            onChange={(e) => updateRow({ notes: e.target.value })}
            className="min-h-7 max-h-28 py-1 text-xs md:text-xs px-1.5 resize-y leading-snug [field-sizing:content] min-w-[120px] 2xl:min-w-[220px]"
            data-testid="order-line-notes"
          />
        ),
      });

      return cols;
    },
    [canAddMasterData, selectedCustomerId],
  );

  return (
    <>
    <form onSubmit={handleFormSubmit} className="space-y-2">
      <form.Subscribe
        selector={(s) => ({
          errors: s.errors,
          values: s.values,
          canSubmit: s.canSubmit,
          isValid: s.isValid,
        })}
      >
        {({ errors, values, canSubmit, isValid }) => (
          <>
            <div id="debug-form-errors" style={{ display: "none" }}>
              {JSON.stringify({ errors, canSubmit, isValid })}
            </div>
            <div id="debug-form-values" style={{ display: "none" }}>
              {JSON.stringify(values)}
            </div>
          </>
        )}
      </form.Subscribe>

      {/* Trường ẩn */}
      <form.Field name="orderDate">
        {(field) => (
          <input type="hidden" value={safeFormatDateInput(field.state.value)} />
        )}
      </form.Field>

      {originalOrderCreatedAt &&
        linkedQuotation?.updatedAt &&
        new Date(linkedQuotation.updatedAt) >
          new Date(originalOrderCreatedAt) && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Cảnh báo Báo giá thay đổi!</AlertTitle>
            <AlertDescription>
              Báo giá liên kết với Đơn hàng này đã được cập nhật sau khi Đơn
              hàng này được tạo. Vui lòng kiểm tra lại đơn giá và các thông tin
              khác để đảm bảo tính nhất quán.
            </AlertDescription>
          </Alert>
        )}

      {/* ═══ SECTION 1: Thông tin đơn hàng + Trạng thái ═══ */}
      <Card className="shadow-sm">
        <CardHeader className="p-3 pb-2 border-b">
          <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
            <FileText className="h-4 w-4 text-emerald-600" /> Thông tin chung
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div
              className={cn(
                "col-span-2",
                !defaultValues?.orderId && "md:col-span-3",
              )}
            >
              <form.Field name="customerId">
                {(field) => (
                  <div className="space-y-1" id="customerId" data-field="customerId">
                    <Label className="flex items-center gap-1">
                      Khách hàng <span className="text-destructive font-bold">*</span>
                    </Label>
                    <SharedCombobox
                      id="customerId"
                      dataField="customerId"
                      required={true}
                      error={hasFieldError(field)}
                      type="customer"
                      value={(field.state.value as string) || ""}
                      valueLabel={form.getFieldValue("customerName") || defaultValues?.customerName || (defaultValues as any)?.customer?.shortName || (defaultValues as any)?.customer?.name}
                      breadcrumb={["Đơn hàng", "Khách hàng"]}
                      onChange={(id, opt) => {
                        field.handleChange(id);
                        setSelectedCustomerId(id);
                        if (opt) {
                          const customer = opt.original;
                          form.setFieldValue(
                            "customerName",
                            (customer.name || customer.shortName || "") as any,
                          );
                          const shouldUpdateContact =
                            form.getFieldValue("updateCustomerInfo") ||
                            !form.getFieldValue("phone");
                          if (shouldUpdateContact) {
                            if (!form.getFieldValue("phone") || form.getFieldValue("updateCustomerInfo")) {
                              form.setFieldValue(
                                "phone",
                                (customer.phone || customer.mobile || "") as any,
                              );
                            }
                            if (!form.getFieldValue("email") || form.getFieldValue("updateCustomerInfo")) {
                              form.setFieldValue(
                                "email",
                                (customer.email || "") as any,
                              );
                            }
                            if (!form.getFieldValue("deliveryAddress") || form.getFieldValue("updateCustomerInfo")) {
                              form.setFieldValue(
                                "deliveryAddress",
                                (customer.deliveryAddress || customer.address || "") as any,
                              );
                            }
                            if (!form.getFieldValue("contactPerson") || form.getFieldValue("updateCustomerInfo")) {
                              form.setFieldValue(
                                "contactPerson",
                                (customer.contactPerson || "") as any,
                              );
                            }
                            if (customer.paymentTerms || (customer as any).paymentTerm) {
                              form.setFieldValue(
                                "paymentTerm",
                                (customer.paymentTerms || (customer as any).paymentTerm) as any,
                              );
                            }
                            if ((customer as any).deposit) {
                              form.setFieldValue(
                                "deposit",
                                (customer as any).deposit as any,
                              );
                            }
                            if ((customer as any).deliveryMethod) {
                              form.setFieldValue(
                                "deliveryMethod",
                                (customer as any).deliveryMethod as any,
                              );
                            }
                          }

                          // Apply tolerance to existing details
                          const newTol =
                            (customer as any).tolerancePercent ?? 1.0;
                          setCurrentTolerance(newTol);
                          const currentDetails = form.getFieldValue(
                            "orderDetails",
                          ) as OrderDetailFormData[];
                          if (currentDetails && currentDetails.length > 0) {
                            form.setFieldValue(
                              "orderDetails",
                              currentDetails.map((d) => ({
                                ...d,
                                allowancePercent:
                                  d.allowancePercent === undefined ||
                                  d.allowancePercent === currentTolerance ||
                                  d.allowancePercent === 0
                                    ? newTol
                                    : d.allowancePercent,
                              })) as any,
                            );
                          }
                        } else {
                          form.setFieldValue("customerName", "" as any);
                        }
                      }}
                      placeholder="Chọn khách hàng..."
                      canAdd={hasPermission("customers", "create")}
                    />
                    {field.state.value && (
                      <div className="flex items-center justify-end mt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px] text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50 font-medium flex items-center gap-1 border border-indigo-200/60 rounded"
                          onClick={() => setShowEditCustomerModal(true)}
                          title="Sửa nhanh thông tin khách hàng này"
                        >
                          <UserCog className="h-3 w-3" />
                          Sửa thông tin KH
                        </Button>
                      </div>
                    )}
                    {showEditCustomerModal && field.state.value && (
                      <CustomerFormModal
                        open={showEditCustomerModal}
                        onClose={() => setShowEditCustomerModal(false)}
                        customerId={field.state.value}
                        mode="edit"
                        breadcrumb={["Đơn hàng", "Khách hàng", "Sửa thông tin"]}
                        onSuccess={(updated) => {
                          const orig = updated.original || {};
                          form.setFieldValue("customerName", (orig.name || orig.shortName || "") as any);
                          if (orig.contactPerson) form.setFieldValue("contactPerson", orig.contactPerson as any);
                          if (orig.phone || orig.mobile) form.setFieldValue("phone", (orig.phone || orig.mobile) as any);
                          if (orig.email) form.setFieldValue("email", orig.email as any);
                          if (orig.deliveryAddress || orig.address) form.setFieldValue("deliveryAddress", (orig.deliveryAddress || orig.address) as any);
                          if (orig.paymentTerms || orig.paymentTerm) form.setFieldValue("paymentTerm", (orig.paymentTerms || orig.paymentTerm) as any);
                          if (orig.deposit) form.setFieldValue("deposit", orig.deposit as any);
                          if (orig.deliveryMethod) form.setFieldValue("deliveryMethod", orig.deliveryMethod as any);
                          toast.success("Đã đồng bộ thông tin khách hàng vào đơn hàng!");
                        }}
                      />
                    )}
                    {!field.state.value && (
                      <form.Field name="customerName">
                        {(nameField) =>
                          nameField.state.value ? (
                            <div className="text-[11px] px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded flex items-center gap-1">
                              <span className="font-semibold text-amber-800">
                                ⚠️ Khách báo giá (chưa có ID):
                              </span>
                              {nameField.state.value as string}
                            </div>
                          ) : null
                        }
                      </form.Field>
                    )}
                  </div>
                )}
              </form.Field>
            </div>
            {defaultValues?.orderId && (
              <form.Field name="orderId">
                {(field) => (
                  <div className="space-y-1 col-span-1">
                    <Label>Mã đơn hàng</Label>
                    <Input
                      value={(field.state.value as string) ?? ""}
                      readOnly
                      className="font-mono bg-white text-indigo-700 font-semibold h-8"
                    />
                  </div>
                )}
              </form.Field>
            )}
            {!defaultValues?.orderId && null}
            <div className="col-span-1">
              <form.Field name="deliveryDeadline">
                {(field) => (
                  <TanstackFieldWrapper field={field} label="Hạn giao hàng" required>
                    <TanstackFormControl>
                      <Input
                        id={field.name}
                        type="date"
                        value={safeFormatDateInput(field.state.value)}
                        className="h-8"
                        onChange={(e) => {
                          const newDeadline = e.target.value;
                          field.handleChange(newDeadline as any);

                          // Cascade to order details that don't have custom delivery dates
                          const currentDetails = form.getFieldValue(
                            "orderDetails",
                          ) as OrderDetailFormData[];
                          if (currentDetails && currentDetails.length > 0) {
                            form.setFieldValue(
                              "orderDetails",
                              currentDetails.map((d: any) => ({
                                ...d,
                                deliveryDeadline: d.isCustomDeliveryDate ? d.deliveryDeadline : newDeadline,
                                deliveryDate: d.isCustomDeliveryDate ? d.deliveryDate : newDeadline,
                              })) as any,
                            );
                          }
                        }}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>
            <div className="space-y-1 col-span-1">
              <form.Field name="priority">
                {(field: any) => (
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1.5">
                      <span>Mức ưu tiên</span>
                      {field.state.value === "URGENT" && (
                        <Flame className="h-3.5 w-3.5 text-rose-600 animate-pulse" />
                      )}
                      {field.state.value === "HIGH" && (
                        <Zap className="h-3.5 w-3.5 text-amber-500" />
                      )}
                    </Label>
                    <Select
                      value={field.state.value || "NORMAL"}
                      onValueChange={(v) => field.handleChange(v)}
                    >
                      <SelectTrigger
                        className={cn(
                          "h-8 font-medium",
                          field.state.value === "URGENT" &&
                            "border-rose-500 text-rose-700 bg-rose-50/50",
                          field.state.value === "HIGH" &&
                            "border-amber-500 text-amber-800 bg-amber-50/50"
                        )}
                        data-testid="order-priority-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(OrderPriority).map(([key, cfg]) => (
                          <SelectItem key={key} value={key}>
                            {cfg.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>
            <div className="space-y-1 col-span-1">
              <form.Field name="category">
                {(field: any) => (
                  <div className="space-y-1">
                    <Label>Phân loại</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v)}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANUFACTURING">Sản xuất</SelectItem>
                        <SelectItem value="OUTSOURCE">Gia công</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>
            <div className="space-y-1 col-span-1">
              <form.Field name="orderType">
                {(field) => (
                  <ComboboxConfigMaster
                    categoryCode="order_types"
                    value={(field.state.value as string) || "PRODUCTION"}
                    onChange={(v) => field.handleChange(v as any)}
                    label="Loại đơn hàng"
                    hideLabel // Label is outside
                  />
                )}
              </form.Field>
            </div>
            <form.Field name="salesUserId">
              {(field) => (
                <div className="space-y-1 col-span-1">
                  <Label>NV phụ trách</Label>
                  <SharedCombobox
                    type="user"
                    value={(field.state.value as string) || ""}
                    onChange={(id) => field.handleChange(id)}
                    placeholder="Chọn NV..."
                  />
                </div>
              )}
            </form.Field>
            <form.Subscribe
              selector={(s) =>
                [s.values.currency, (s.values as any).exchangeRate] as const
              }
            >
              {([currency, exchangeRateVal]) => (
                <div className="space-y-1 col-span-2 md:col-span-1">
                  <Label>Tiền tệ / Tỷ giá</Label>
                  <CurrencyExchangeField
                    currency={currency || "VND"}
                    exchangeRate={exchangeRateVal || 1}
                    onCurrencyChange={(v, _rate) =>
                      form.setFieldValue("currency", v as any)
                    }
                    onExchangeRateChange={(v) =>
                      form.setFieldValue("exchangeRate", v as any)
                    }
                    hideLabel
                  />
                </div>
              )}
            </form.Subscribe>
            <div className="col-span-2 md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-slate-100 mt-1">
              <div className="col-span-2 md:col-span-1">
                <form.Field name="contactPerson">
                  {(field) => (
                    <TanstackFieldWrapper field={field} label="Người liên hệ">
                      <TanstackFormControl>
                        <Input
                          id={field.name}
                          type="text"
                          value={(field.state.value as any) ?? ""}
                          placeholder="Nguyễn Văn A"
                          className="h-8"
                          onChange={(e) =>
                            field.handleChange(e.target.value as any)
                          }
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                </form.Field>
              </div>
              <div className="col-span-2 md:col-span-1">
                <form.Field name="phone">
                  {(field) => (
                    <TanstackFieldWrapper field={field} label="Số điện thoại">
                      <TanstackFormControl>
                        <Input
                          id={field.name}
                          type="text"
                          value={(field.state.value as any) ?? ""}
                          placeholder="090..."
                          className="h-8"
                          onChange={(e) =>
                            field.handleChange(e.target.value as any)
                          }
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                </form.Field>
              </div>
              <div className="col-span-2 md:col-span-1">
                <form.Field name="email">
                  {(field) => (
                    <TanstackFieldWrapper field={field} label="Email">
                      <TanstackFormControl>
                        <Input
                          id={field.name}
                          type="text"
                          value={(field.state.value as any) ?? ""}
                          placeholder="abc@abc.com"
                          className="h-8"
                          onChange={(e) =>
                            field.handleChange(e.target.value as any)
                          }
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                </form.Field>
              </div>
              <div className="col-span-2 md:col-span-1 flex items-end pb-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  onClick={async () => {
                    const custId = form.getFieldValue("customerId" as any);
                    if (!custId) {
                      toast.error("Vui lòng chọn khách hàng trước");
                      return;
                    }
                    try {
                      await trpcClient.customers.update.mutate({
                        id: custId as string,
                        data: {
                          contactPerson:
                            form.getFieldValue("contactPerson" as any) || null,
                          phone: form.getFieldValue("phone" as any) || null,
                          email: form.getFieldValue("email" as any) || null,
                          address:
                            form.getFieldValue("deliveryAddress" as any) ||
                            null,
                        },
                      });
                      toast.success("Đã lưu thông tin liên hệ cho Khách hàng");
                    } catch (e: any) {
                      toast.error(
                        "Lỗi: " + (e.message || "Không thể cập nhật"),
                      );
                    }
                  }}
                >
                  <Save className="w-3.5 h-3.5 mr-1" />
                  Lưu thông tin liên hệ
                </Button>
              </div>
            </div>

            <form.Subscribe selector={(s) => (s.values as any).orderType}>
              {(orderType) =>
                orderType === "SAMPLE" ? (
                  <div className="space-y-1 col-span-2 md:col-span-4 mt-2">
                    <Label>Loại mẫu</Label>
                    <Select
                      value={
                        (form.getFieldValue("sampleType" as any) as string) ||
                        "INTERNAL_FREE"
                      }
                      onValueChange={(v) =>
                        form.setFieldValue("sampleType" as any, v as any)
                      }
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SampleType).map(([key, cfg]) => (
                          <SelectItem key={key} value={key}>
                            {cfg.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null
              }
            </form.Subscribe>
          </div>
        </CardContent>
      </Card>

      {/* ═══ SECTION 2: Chi tiết đơn hàng + Tổng hợp tài chính ═══ */}
      <Card className="shadow-sm">
        <CardHeader className="p-3 pb-2 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
              <Package className="h-4 w-4 text-slate-700" /> Chi tiết đơn hàng <span className="text-destructive font-bold">*</span>
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs md:text-xs gap-1"
                onClick={() =>
                  downloadExcelTemplate(
                    DETAIL_EXPORT_COLUMNS,
                    "chi_tiet_don_hang",
                    ORDER_SAMPLE_DATA,
                  )
                }
              >
                <FileDown className="h-3.5 w-3.5" /> Tải mẫu Excel
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs md:text-xs gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                disabled={importing}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" />
                {importing ? "Đang import..." : "Import Excel"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleImportDetails}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 pt-3">
          <form.Subscribe
            selector={(s) =>
              [
                s.values.currency,
                s.values.vatRate,
                (s.values as any).exchangeRate,
                // getDetailColumns adds "Giá mua"/"Giá gốc" by category, so the table must re-render on category change (FB-UJLTPN)
                (s.values as any).category,
              ] as const
            }
          >
            {([currency, vatRateVal, exchangeRateVal]) => (
              <form.Field name="orderDetails">
                {(field) => {
                  const hasErr = field.state.meta.errors.length > 0;
                  return (
                    <div
                      id="orderDetails"
                      data-field="orderDetails"
                      className={cn(
                        "transition-all duration-200",
                        hasErr && "rounded-lg border-2 border-destructive p-1 ring-2 ring-destructive/30"
                      )}
                    >
                      <InlineDetailTable<OrderDetailFormData>
                      columns={getDetailColumns(currency as any)}
                      rows={(field.state.value as OrderDetailFormData[]) || []}
                      onChange={(newRows) => {
                        const prevRows = (
                          (field.state.value as OrderDetailFormData[]) || []
                        ).map((r) => ({ ...r }));
                        field.handleChange(newRows as any);
                        costEstimatorRef.current?.scheduleChangedRows(
                          prevRows,
                          newRows,
                        );
                      }}
                      createEmptyRow={() => ({
                        ...EMPTY_DETAIL,
                        allowancePercent: currentTolerance,
                        deliveryDeadline:
                          form.getFieldValue("deliveryDeadline"),
                        deliveryDate:
                          form.getFieldValue("deliveryDeadline"),
                      })}
                      minRows={1}
                      stickyFirstColumn
                      footer={(rows) => {
                        const cols = getDetailColumns(currency as any);
                        const qtyColIndex = cols.findIndex(
                          (c) => c.key === "quantity",
                        );
                        const amountColIndex = cols.findIndex(
                          (c) => c.key === "amount",
                        );
                        const totalQty = rows.reduce(
                          (s, d) => s + Number(d.quantity || 0),
                          0,
                        );
                        const subtotal = rows.reduce(
                          (s, d) =>
                            s +
                            Number(d.quantity || 0) *
                              (currency !== "VND"
                                ? Number(d.priceUSD || 0)
                                : Number(d.unitPrice || 0)),
                          0,
                        );
                        const totalCost = rows.reduce(
                          (s, d) =>
                            s +
                            Number(d.costPrice || 0) * Number(d.quantity || 0),
                          0,
                        );
                        const margin =
                          subtotal > 0
                            ? ((subtotal - totalCost) / subtotal) * 100
                            : 0;

                        const vatRate = Number(vatRateVal || 0);
                        const vatAmount = (subtotal * vatRate) / 100;
                        const shippingFee = Number(
                          form.getFieldValue("shippingFee") || 0,
                        );
                        const grandTotal = subtotal + vatAmount + shippingFee;

                        const renderMoney = (amount: number) => {
                          if (currency !== "VND")
                            return Number(amount).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            });
                          return formatMoney(amount);
                        };

                        const leftSpan = qtyColIndex + 1;
                        const spacerSpan = amountColIndex - qtyColIndex - 1;
                        const rightSpan = cols.length - amountColIndex;

                        return (
                          <>
                            {/* Subtotal row with RowSpan details on the left */}
                            <tr className="bg-slate-100/50 border-t border-slate-200">
                              <td
                                colSpan={leftSpan}
                                rowSpan={5}
                                className="p-3 border-r border-slate-200 align-top bg-slate-50/40"
                              >
                                {/* Bounded width + stacked fields: this block must not stretch the line-item columns (FB-LWU401 & FB-90M4OH) */}
                                <div className="font-normal text-left space-y-2 w-full max-w-[380px]">
                                  <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5 border-b pb-1.5">
                                    <Truck className="h-3.5 w-3.5 text-emerald-600" />{" "}
                                    Thanh toán & Giao hàng
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                        Thanh toán
                                      </Label>
                                      <form.Field name="paymentTerm">
                                        {(field) => (
                                          <ComboboxConfigMaster
                                            categoryCode="payment_terms"
                                            value={
                                              (field.state.value as string) ||
                                              ""
                                            }
                                            onChange={(v) =>
                                              field.handleChange(v as any)
                                            }
                                            placeholder="Chọn đ.khoản..."
                                            hideLabel={true}
                                          />
                                        )}
                                      </form.Field>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                        Đặt cọc
                                      </Label>
                                      <form.Field name="deposit">
                                        {(field) => (
                                          <ComboboxConfigMaster
                                            categoryCode="deposit_options"
                                            value={
                                              (field.state.value as string) ||
                                              ""
                                            }
                                            onChange={(v) =>
                                              field.handleChange(v as any)
                                            }
                                            placeholder="Chọn đ.cọc..."
                                            hideLabel={true}
                                          />
                                        )}
                                      </form.Field>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                        Hình thức giao
                                      </Label>
                                      <form.Field name="deliveryMethod">
                                        {(field) => (
                                          <ComboboxConfigMaster
                                            categoryCode="delivery_methods"
                                            value={
                                              (field.state.value as string) ||
                                              ""
                                            }
                                            onChange={(v) =>
                                              field.handleChange(v as any)
                                            }
                                            placeholder="Chọn h.thức..."
                                            hideLabel={true}
                                          />
                                        )}
                                      </form.Field>
                                    </div>
                                    <div className="sm:col-span-2 space-y-1">
                                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                                        Địa chỉ giao hàng
                                      </Label>
                                      <form.Field name="deliveryAddress">
                                        {(field) => (
                                          <Input
                                            value={
                                              (field.state.value as string) ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              field.handleChange(
                                                e.target.value as any,
                                              )
                                            }
                                            placeholder="Nhập địa chỉ giao hàng..."
                                            className="h-8 text-xs"
                                          />
                                        )}
                                      </form.Field>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right font-bold text-slate-800 text-[13px] border-b border-slate-200">
                                {totalQty.toLocaleString("vi-VN")}
                              </td>
                              <td
                                colSpan={spacerSpan}
                                className="px-3 py-2 text-right font-semibold text-slate-700 text-xs uppercase border-b border-slate-200"
                              >
                                Tổng doanh thu (Subtotal)
                              </td>
                              <td className="px-2 py-2 text-right font-mono text-slate-800 font-bold text-[13px] border-b border-slate-200">
                                {renderMoney(subtotal)}
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="border-b border-slate-200"
                              ></td>
                            </tr>

                            {/* Total Cost row */}
                            <tr className="bg-orange-50/30">
                              <td
                                colSpan={spacerSpan + 1}
                                className="px-3 py-1.5 text-right font-semibold text-amber-700 text-xs uppercase border-b border-slate-200"
                              >
                                Giá vốn (Cost)
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono text-amber-700 font-bold text-[13px] border-b border-slate-200">
                                {renderMoney(totalCost)}
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="border-b border-slate-200"
                              ></td>
                            </tr>

                            {/* Margin row */}
                            <tr className="bg-emerald-50/30">
                              <td
                                colSpan={spacerSpan + 1}
                                className="px-3 py-1.5 text-right font-semibold text-emerald-700 text-xs uppercase border-b border-slate-200"
                              >
                                Biên lợi nhuận (Margin)
                              </td>
                              <td className="px-2 py-1.5 text-right font-mono text-emerald-700 font-bold text-[13px] border-b border-slate-200">
                                {margin.toLocaleString("vi-VN", {
                                  maximumFractionDigits: 2,
                                })}
                                %
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="border-b border-slate-200"
                              ></td>
                            </tr>

                            {/* VAT row */}
                            <tr>
                              <td
                                colSpan={spacerSpan + 1}
                                className="px-2 py-2 text-right border-b border-slate-200"
                              >
                                <div className="flex justify-end items-center gap-3">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[11px] font-normal text-slate-400 normal-case">
                                      Chọn VAT:
                                    </span>
                                    <div className="w-24 text-left">
                                      <NumericInput
                                        value={vatRateVal ?? 8}
                                        onChange={(v) =>
                                          form.setFieldValue(
                                            "vatRate",
                                            Number(v || 0) as any,
                                          )
                                        }
                                        className="h-7 w-full text-right px-2 font-mono bg-white"
                                        decimals={2}
                                      />
                                    </div>
                                  </div>
                                  <span className="text-slate-600 text-xs uppercase font-semibold">
                                    Thuế VAT ({vatRate}%)
                                  </span>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right font-mono text-slate-700 font-bold text-[13px] border-b border-slate-200">
                                {renderMoney(vatAmount)}
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="border-b border-slate-200"
                              ></td>
                            </tr>

                            {/* Shipping Fee row */}
                            <tr>
                              <td
                                colSpan={spacerSpan + 1}
                                className="px-2 py-2 text-right border-b border-slate-200"
                              >
                                <div className="flex justify-end items-center gap-3">
                                  <span className="text-slate-600 text-xs uppercase font-semibold">
                                    Phí giao hàng (Shipping)
                                  </span>
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right font-mono text-slate-700 font-bold text-[13px] border-b border-slate-200">
                                <form.Field name="shippingFee">
                                  {(field) => (
                                    <NumericInput
                                      value={field.state.value as number}
                                      onChange={(val) =>
                                        field.handleChange(
                                          (Number(val) || 0) as any,
                                        )
                                      }
                                      className="w-24 text-right h-7 text-xs font-mono inline-block"
                                      placeholder="0"
                                    />
                                  )}
                                </form.Field>
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="border-b border-slate-200"
                              ></td>
                            </tr>

                            {/* Grand Total row */}
                            <tr className="bg-indigo-50/10 border-t border-indigo-100">
                              <td
                                colSpan={spacerSpan + 1}
                                className="px-3 py-3 text-right bg-indigo-50/20"
                              >
                                <span className="text-slate-800 text-xs uppercase font-bold">
                                  Tổng cộng (Grand Total)
                                </span>
                              </td>
                              <td className="px-2 py-3 text-right font-mono font-black text-indigo-700 text-[15px] bg-indigo-50/30">
                                {renderMoney(grandTotal)}
                              </td>
                              <td
                                colSpan={rightSpan}
                                className="bg-indigo-50/20"
                              ></td>
                            </tr>
                          </>
                        );
                      }}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <div className="text-[0.8rem] font-medium text-destructive mt-1">
                        {field.state.meta.errors.map((e: any) => typeof e === 'object' && e ? e.message || JSON.stringify(e) : String(e)).join(", ")}
                      </div>
                    )}
                  </div>
                );
              }}
            </form.Field>
            )}
          </form.Subscribe>
        </CardContent>
      </Card>

      {/* ═══ SECTION 4: Ghi chú & Comments ═══ */}
      <Card className="shadow-sm">
        <CardHeader className="p-3 pb-2 border-b">
          <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
            <Info className="h-4 w-4 text-emerald-600" /> Ghi chú & Comments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-3">
          <form.Field name="notes">
            {(field) => (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-600">
                    Ghi chú (chọn nhanh)
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Hàng giao gấp",
                      "Sản xuất cẩn thận",
                      "Gọi trước khi giao",
                      "Đóng gói kỹ",
                    ].map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 text-xs font-normal text-slate-600 py-1 px-2"
                        onClick={() => {
                          const current = (field.state.value as string) || "";
                          const append = current
                            ? current.trim().endsWith(";")
                              ? ` ${tag}`
                              : `; ${tag}`
                            : tag;
                          field.handleChange(current + append);
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5 mt-2">
                  <Label
                    htmlFor={field.name}
                    className="text-xs font-semibold text-slate-600"
                  >
                    Hộp comments
                  </Label>
                  <Textarea
                    id={field.name}
                    value={(field.state.value as string) ?? ""}
                    rows={3}
                    placeholder="Nhập comments hoặc paste ảnh trực tiếp..."
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    onPaste={async (e) => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (let i = 0; i < items.length; i++) {
                        const item = items[i];
                        if (item.type.indexOf("image") !== -1) {
                          const file = item.getAsFile();
                          if (!file) continue;
                          e.preventDefault();
                          try {
                            validateUploadFile(file, {
                              maxSize: MAX_MEDIA_IMAGE_SIZE,
                              label: "Ảnh dán từ clipboard",
                            });
                            const toastId = toast.loading("Đang tải ảnh lên...");
                            try {
                              const uploadedUrl = await uploadFile(file);
                              const fullUrl = getFullUrl(uploadedUrl.url);
                              const markdownImage = `![image](${fullUrl})`;
                              const current = (field.state.value as string) || "";
                              const append = current
                                ? `${current}\n${markdownImage}`
                                : markdownImage;
                              field.handleChange(append);
                              toast.dismiss(toastId);
                            } catch (error) {
                              toast.error(extractErrorMessage(error, "Lỗi tải ảnh lên"), { id: toastId });
                            }
                          } catch (valErr) {
                            toast.error(extractErrorMessage(valErr));
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </form.Field>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 z-50 flex flex-row justify-between items-center -mx-4 -mb-4 mt-6 shadow-[0_-10px_15px_-3px_rgb(0,0,0,0.05)] rounded-b-lg">
        <form.Subscribe
          selector={(s) =>
            ({ fieldMeta: s.fieldMeta, errorMap: s.errorMap, submissionAttempts: s.submissionAttempts }) as any
          }
        >
          {(formState: any) => {
            const messages =
              formState.submissionAttempts > 0 ? collectFormErrorMessages(formState) : [];
            if (messages.length === 0) return <div />;
            return (
              <div
                role="alert"
                className="text-red-600 text-xs font-medium px-2 max-h-20 overflow-y-auto"
              >
                <div className="font-semibold">Còn thiếu hoặc chưa hợp lệ:</div>
                <ul className="list-disc pl-4">
                  {messages.slice(0, 6).map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                  {messages.length > 6 && <li>và {messages.length - 6} mục khác</li>}
                </ul>
              </div>
            );
          }}
        </form.Subscribe>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="whitespace-nowrap h-9"
          >
            Huỷ
          </Button>
          <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
            {([, submitting]) => (
              <SaveActionButtons
                status={currentStatus}
                busy={Boolean(submitting || isSubmitting || isDraftSaving)}
                onSaveDraft={handleSaveDraft}
                submitLabel={submitLabel}
              />
            )}
          </form.Subscribe>
        </div>
      </div>
    </form>
      {editingProductId && (
        <ProductEditModal
          productId={editingProductId}
          isOpen={true}
          breadcrumb={["Đơn hàng", "Sản phẩm", "Chỉnh sửa sản phẩm"]}
          onClose={() => {
            setEditingProductId(null);
            setEditingProductRowIndex(null);
          }}
          onSuccess={(updated) => {
            if (updated && editingProductRowIndex !== null) {
              const currentRows = (form.getFieldValue("orderDetails") as OrderDetailFormData[]) || [];
              if (currentRows[editingProductRowIndex]) {
                const updatedRows = [...currentRows];
                const specsStr = formatSpecs(updated.specs, updated) || updated.packingSpec || updatedRows[editingProductRowIndex].specs;
                const newPrice = updated.price ? Number(updated.price) : updatedRows[editingProductRowIndex].unitPrice;
                const qty = updatedRows[editingProductRowIndex].quantity || 0;
                updatedRows[editingProductRowIndex] = {
                  ...updatedRows[editingProductRowIndex],
                  productName: updated.productName || updated.name || updatedRows[editingProductRowIndex].productName,
                  productCode: updated.productCode || updated.code || updatedRows[editingProductRowIndex].productCode,
                  specs: specsStr,
                  unit: updated.unit || updatedRows[editingProductRowIndex].unit,
                  unitPrice: newPrice,
                  amount: qty * newPrice,
                };
                form.setFieldValue("orderDetails", updatedRows as any);
              }
            }
            setEditingProductId(null);
            setEditingProductRowIndex(null);
          }}
        />
      )}
      {cloningProduct && (
        <ProductFormModal
          open={!!cloningProduct}
          initialData={{
            productCode: `${cloningProduct.productCode || "SP"}-COPY`,
            productName: `${cloningProduct.productName || "Sản phẩm"} (Bản sao)`,
            customerId: cloningProduct.customerId || selectedCustomerId || undefined,
          }}
          breadcrumb={["Đơn hàng", "Chi tiết", "Nhân bản sản phẩm"]}
          onClose={() => {
            setCloningProduct(null);
            setCloningRowIndex(null);
          }}
          onSuccess={handleCloneProductSuccess}
        />
      )}
      <SmartImportStagingModal
        open={stagingModalOpen}
        onOpenChange={setStagingModalOpen}
        rawItems={stagingRawRows}
        customerId={selectedCustomerId}
        fileName={importFileName}
        scope="ORDER"
        onSuccess={handleStagingSuccess}
      />
    </>
  );
}
